import type { SiteIconKey } from "@/design-system/tokens/iconography";
import {
  CONSOLE_PERMISSIONS,
  meetsPermissionRequirement,
  type PermissionRequirement,
} from "@/constants/permissions";

type ConsoleNavItemId =
  | "pings"
  | "tracker"
  | "treatment-overview"
  | "patients"
  | "appointments"
  | "staff"
  | "communications"
  | "consultation-recordings"
  | "transactions"
  | "maintenance-windows"
  | "services"
  | "promo-codes"
  | "medicine"
  | "nutrition"
  | "diseases";

export type ConsoleNavigationChild = {
  id: string;
  label: string;
  href: string;
};

type ConsoleNavigationItem = {
  id: ConsoleNavItemId;
  label: string;
  tooltip: string;
  href: string;
  iconKey: SiteIconKey;
  children?: readonly ConsoleNavigationChild[];
  permissionRequirement: PermissionRequirement;
};

const homePermissionRequirement = {
  permissions: [CONSOLE_PERMISSIONS.HOME_DASHBOARD],
} as const satisfies PermissionRequirement;

const pingsPermissionRequirement = {
  permissions: [CONSOLE_PERMISSIONS.VIEW_PINGS],
} as const satisfies PermissionRequirement;

const patientsPermissionRequirement = {
  permissions: [CONSOLE_PERMISSIONS.VIEW_PATIENTS],
} as const satisfies PermissionRequirement;

const trackerPermissionRequirement = {
  permissions: [CONSOLE_PERMISSIONS.VIEW_TRACKER],
} as const satisfies PermissionRequirement;

const treatmentOverviewPermissionRequirement = {
  permissions: [CONSOLE_PERMISSIONS.VIEW_TREATMENT_OVERVIEW],
} as const satisfies PermissionRequirement;

export const consoleHomeNavigation = {
  id: "home",
  label: "Home",
  tooltip: "Home",
  href: "/home",
  permissionRequirement: homePermissionRequirement,
} as const;

export const consoleDashboardNavigation = [
  {
    id: "pings",
    label: "Pings",
    tooltip: "Pings",
    href: "/dashboard",
    iconKey: "messageSquare",
    permissionRequirement: pingsPermissionRequirement,
  },
  {
    id: "appointments",
    label: "Appointments",
    tooltip: "Appointments",
    href: "/settings/appointments/calendar",
    iconKey: "calendar",
    permissionRequirement: {
      permissions: [CONSOLE_PERMISSIONS.VIEW_APPOINTMENTS],
    },
  },
] as const satisfies readonly ConsoleNavigationItem[];

export const consoleCareNavigation = [
  {
    id: "patients",
    label: "Patients",
    tooltip: "Patients",
    href: "/patients",
    iconKey: "users",
    permissionRequirement: patientsPermissionRequirement,
  },
  {
    id: "tracker",
    label: "Trackers",
    tooltip: "Trackers",
    href: "/tracker",
    iconKey: "activity",
    permissionRequirement: trackerPermissionRequirement,
  },
  {
    id: "treatment-overview",
    label: "Treatment History",
    tooltip: "Treatment History",
    href: "/treatment-overview",
    iconKey: "fileBarChart",
    permissionRequirement: treatmentOverviewPermissionRequirement,
  },
] as const satisfies readonly ConsoleNavigationItem[];

export const consoleManagementNavigation = [
  {
    id: "staff",
    label: "Staff",
    tooltip: "Staff",
    href: "/settings/staff/view-staff",
    iconKey: "userCog",
    permissionRequirement: {
      permissions: [CONSOLE_PERMISSIONS.MANAGE_STAFF],
    },
  },
  {
    id: "communications",
    label: "Communications",
    tooltip: "Communications",
    href: "/settings/communications/logs",
    iconKey: "radio",
    permissionRequirement: {
      permissions: [CONSOLE_PERMISSIONS.MANAGE_COMMUNICATIONS],
    },
  },
  {
    id: "consultation-recordings",
    label: "Recordings",
    tooltip: "Consultation Recordings",
    href: "/settings/consultation-recordings/overview",
    iconKey: "fileText",
    permissionRequirement: {
      permissions: [CONSOLE_PERMISSIONS.VIEW_CONSULTATION_RECORDINGS],
    },
  },
  {
    id: "transactions",
    label: "Transactions",
    tooltip: "Transactions",
    href: "/settings/transactions/logs",
    iconKey: "fileText",
    permissionRequirement: {
      permissions: [CONSOLE_PERMISSIONS.VIEW_PAYMENTS],
    },
  },
  {
    id: "maintenance-windows",
    label: "Maintenance Window",
    tooltip: "Maintenance Window",
    href: "/settings/maintenance-windows",
    iconKey: "calendar",
    permissionRequirement: {
      permissions: [
        CONSOLE_PERMISSIONS.VIEW_MAINTENANCE,
        CONSOLE_PERMISSIONS.MANAGE_MAINTENANCE,
      ],
    },
  },
  {
    id: "services",
    label: "Services",
    tooltip: "Services",
    href: "/settings/services/manage-services",
    iconKey: "layers",
    permissionRequirement: {
      permissions: [
        CONSOLE_PERMISSIONS.VIEW_SERVICES,
        CONSOLE_PERMISSIONS.MANAGE_SERVICES,
      ],
    },
  },
  {
    id: "promo-codes",
    label: "Promo Codes",
    tooltip: "Promo Codes",
    href: "/settings/promo-codes/manage-promo-codes",
    iconKey: "plusCircle",
    permissionRequirement: {
      permissions: [CONSOLE_PERMISSIONS.MANAGE_PROMO_CODES],
    },
  },
] as const satisfies readonly ConsoleNavigationItem[];

export const consoleDatabaseNavigation = [
  {
    id: "medicine",
    label: "Medications",
    tooltip: "Medications",
    href: "/settings/medicine/add-medicine",
    iconKey: "pill",
    permissionRequirement: {
      permissions: [CONSOLE_PERMISSIONS.MANAGE_MEDICINE_DB],
    },
  },
  {
    id: "nutrition",
    label: "Foods",
    tooltip: "Foods",
    href: "/settings/nutrition/view-food-items",
    iconKey: "apple",
    permissionRequirement: {
      permissions: [CONSOLE_PERMISSIONS.MANAGE_NUTRITION_DB],
    },
    children: [
      {
        id: "view-food-items",
        label: "Food Items",
        href: "/settings/nutrition/view-food-items",
      },
      {
        id: "view-recipes",
        label: "Recipes",
        href: "/settings/nutrition/view-recipes",
      },
      {
        id: "food-brands",
        label: "Brands",
        href: "/settings/nutrition/food-brands",
      },
      {
        id: "food-sources",
        label: "Sources",
        href: "/settings/nutrition/food-sources",
      },
      {
        id: "serving-units",
        label: "Serving Units",
        href: "/settings/nutrition/serving-units",
      },
      {
        id: "food-servings",
        label: "Food Servings",
        href: "/settings/nutrition/food-servings",
      },
    ],
  },
  {
    id: "diseases",
    label: "Diseases",
    tooltip: "Diseases",
    href: "/settings/diseases/view-diseases",
    iconKey: "clipboardList",
    permissionRequirement: {
      permissions: [CONSOLE_PERMISSIONS.MANAGE_DISEASE_DB],
    },
  },
] as const satisfies readonly ConsoleNavigationItem[];

export type ConsoleSettingsSection = {
  id:
    | "appointments"
    | "medicine"
    | "nutrition"
    | "staff"
    | "communications"
    | "consultation-recordings"
    | "transactions"
    | "services"
    | "promo-codes"
    | "diseases";
  label: string;
  iconKey: SiteIconKey;
  subSections?: readonly {
    id: string;
    label: string;
  }[];
};

export const consoleSettingsSections = [
  {
    id: "appointments",
    label: "Appointments",
    iconKey: "calendar",
    subSections: [
      { id: "calendar", label: "Calendar" },
      { id: "awaiting-payment", label: "Awaiting Payment" },
      { id: "add-appointment", label: "Add Appointment" },
      { id: "blocked-time", label: "Blocked Time" },
    ],
  },
  {
    id: "medicine",
    label: "Medications",
    iconKey: "pill",
    subSections: [{ id: "add-medicine", label: "Add New Medication" }],
  },
  {
    id: "nutrition",
    label: "Foods",
    iconKey: "apple",
    subSections: [
      { id: "view-food-items", label: "View Food Items" },
      { id: "view-recipes", label: "View Recipes" },
      { id: "serving-units", label: "Serving Units" },
      { id: "add-food-entry", label: "Add Food Entry" },
      { id: "update-food-entry", label: "Edit Food" },
      { id: "food-servings", label: "Food Servings" },
      { id: "food-sources", label: "Food Sources" },
      { id: "food-brands", label: "Food Brands" },
    ],
  },
  {
    id: "diseases",
    label: "Diseases",
    iconKey: "clipboardList",
    subSections: [
      { id: "view-diseases", label: "View Diseases" },
      { id: "add-disease", label: "Add Disease" },
    ],
  },
  {
    id: "staff",
    label: "Staff",
    iconKey: "userCog",
    subSections: [
      { id: "view-staff", label: "View Staff" },
      { id: "create-staff", label: "Add New Staff" },
    ],
  },
  {
    id: "communications",
    label: "Comms",
    iconKey: "radio",
    subSections: [{ id: "logs", label: "Comm Logs" }],
  },
  {
    id: "consultation-recordings",
    label: "Consultation Recordings",
    iconKey: "fileText",
    subSections: [{ id: "overview", label: "Overview" }],
  },
  {
    id: "transactions",
    label: "Transactions",
    iconKey: "fileText",
    subSections: [{ id: "logs", label: "Transactions" }],
  },
  {
    id: "services",
    label: "Services",
    iconKey: "layers",
    subSections: [{ id: "manage-services", label: "Manage Services" }],
  },
  {
    id: "promo-codes",
    label: "Promo Codes",
    iconKey: "plusCircle",
    subSections: [{ id: "manage-promo-codes", label: "Manage Promo Codes" }],
  },
] as const satisfies readonly ConsoleSettingsSection[];

const appointmentReadRequirement: PermissionRequirement = {
  permissions: [CONSOLE_PERMISSIONS.VIEW_APPOINTMENTS],
};

const appointmentManageRequirement: PermissionRequirement = {
  permissions: [CONSOLE_PERMISSIONS.MANAGE_APPOINTMENTS],
};

type ConsoleSettingsAccessRule = {
  default: PermissionRequirement;
  subSections?: Readonly<Record<string, PermissionRequirement>>;
};

export const consoleSettingsAccessRules: Readonly<
  Record<string, ConsoleSettingsAccessRule>
> = {
  appointments: {
    default: appointmentManageRequirement,
    subSections: {
      calendar: appointmentReadRequirement,
      list: appointmentReadRequirement,
      "awaiting-payment": appointmentManageRequirement,
      "add-appointment": appointmentManageRequirement,
      "blocked-time": appointmentManageRequirement,
    },
  },
  medicine: {
    default: {
      permissions: [CONSOLE_PERMISSIONS.MANAGE_MEDICINE_DB],
    },
  },
  nutrition: {
    default: {
      permissions: [CONSOLE_PERMISSIONS.MANAGE_NUTRITION_DB],
    },
  },
  diseases: {
    default: {
      permissions: [CONSOLE_PERMISSIONS.MANAGE_DISEASE_DB],
    },
  },
  staff: {
    default: {
      permissions: [CONSOLE_PERMISSIONS.MANAGE_STAFF],
    },
  },
  communications: {
    default: {
      permissions: [CONSOLE_PERMISSIONS.MANAGE_COMMUNICATIONS],
    },
  },
  "consultation-recordings": {
    default: {
      permissions: [CONSOLE_PERMISSIONS.VIEW_CONSULTATION_RECORDINGS],
    },
  },
  transactions: {
    default: {
      permissions: [CONSOLE_PERMISSIONS.VIEW_PAYMENTS],
    },
  },
  "maintenance-windows": {
    default: {
      permissions: [
        CONSOLE_PERMISSIONS.VIEW_MAINTENANCE,
        CONSOLE_PERMISSIONS.MANAGE_MAINTENANCE,
      ],
    },
  },
  services: {
    default: {
      permissions: [
        CONSOLE_PERMISSIONS.VIEW_SERVICES,
        CONSOLE_PERMISSIONS.MANAGE_SERVICES,
      ],
    },
  },
  programs: {
    default: {
      permissions: [
        CONSOLE_PERMISSIONS.VIEW_PROGRAM,
        CONSOLE_PERMISSIONS.MANAGE_PROGRAM,
      ],
    },
  },
  "promo-codes": {
    default: {
      permissions: [CONSOLE_PERMISSIONS.MANAGE_PROMO_CODES],
    },
  },
};

export const consoleRoutePermissionRequirements = {
  home: homePermissionRequirement,
  pings: pingsPermissionRequirement,
  tracker: trackerPermissionRequirement,
  treatmentOverview: treatmentOverviewPermissionRequirement,
  patients: patientsPermissionRequirement,
  createPatient: {
    permissions: [CONSOLE_PERMISSIONS.MANAGE_USERS],
  },
  settings: {
    permissions: [
      CONSOLE_PERMISSIONS.VIEW_APPOINTMENTS,
      CONSOLE_PERMISSIONS.MANAGE_APPOINTMENTS,
      CONSOLE_PERMISSIONS.MANAGE_STAFF,
      CONSOLE_PERMISSIONS.MANAGE_COMMUNICATIONS,
      CONSOLE_PERMISSIONS.VIEW_MAINTENANCE,
      CONSOLE_PERMISSIONS.MANAGE_MAINTENANCE,
      CONSOLE_PERMISSIONS.VIEW_CONSULTATION_RECORDINGS,
      CONSOLE_PERMISSIONS.VIEW_PAYMENTS,
      CONSOLE_PERMISSIONS.VIEW_SERVICES,
      CONSOLE_PERMISSIONS.MANAGE_SERVICES,
      CONSOLE_PERMISSIONS.VIEW_PROGRAM,
      CONSOLE_PERMISSIONS.MANAGE_PROGRAM,
      CONSOLE_PERMISSIONS.MANAGE_PROMO_CODES,
      CONSOLE_PERMISSIONS.MANAGE_NUTRITION_DB,
      CONSOLE_PERMISSIONS.MANAGE_MEDICINE_DB,
      CONSOLE_PERMISSIONS.MANAGE_DISEASE_DB,
      CONSOLE_PERMISSIONS.MANAGE_USERS,
    ],
  },
} as const satisfies Record<string, PermissionRequirement>;

export const getConsoleSettingsPermissionRequirement = (
  section: string,
  subSection: string
) => {
  const rule = consoleSettingsAccessRules[section];

  return rule?.subSections?.[subSection] ?? rule?.default ?? null;
};

type PermissionCheckers = {
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: readonly string[]) => boolean;
};

export const getFirstAccessibleConsoleRoute = ({
  hasPermission,
  hasAnyPermission,
}: PermissionCheckers): string | null => {
  const navigationItems = [
    consoleHomeNavigation,
    ...consoleDashboardNavigation,
    ...consoleCareNavigation,
    ...consoleManagementNavigation,
    ...consoleDatabaseNavigation,
  ];

  const firstAccessibleItem = navigationItems.find((item) =>
    meetsPermissionRequirement(item.permissionRequirement, {
      hasPermission,
      hasAnyPermission,
    })
  );

  return firstAccessibleItem?.href ?? null;
};
